import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSettingsStore } from '../stores/settingsStore';
import { usePortfoliosStore } from '../stores/portfoliosStore';
import { useStrategyBuilderStore } from '../stores/strategyBuilderStore';
import { useBacktestStore } from '../stores/backtestStore';
import { useStrategyStore } from '../stores/strategyStore';
import type { MarketId } from '../components/common/LockedState';

/**
 * Routes a page renders as its full content (not a LockedState). When the
 * user switches to a non-spot market while sitting on one of these pages,
 * we bounce them to the dashboard so they don't stare at a disabled panel
 * wondering what just happened.
 */
const SPOT_ONLY_ROUTE_PREFIXES = [
  '/trading',
  '/journal',
  '/strategy',
  '/backtest',
  '/wallet',
];

function spotOnlyRootFor(pathname: string): string | null {
  for (const p of SPOT_ONLY_ROUTE_PREFIXES) {
    if (pathname === p || pathname.startsWith(p + '/')) return p;
  }
  return null;
}

/**
 * Returns a setter that, in addition to updating the selected market,
 * resets any per-page transient state that belonged to the previous
 * market. This stops stale results from bleeding across market switches
 * (e.g. backtest chart from spot lingering after switching to margin).
 *
 * When the user is sitting on a spot-only trading page at the moment of
 * the switch, we also redirect them back to Dashboard — leaving them on
 * a LockedState with no content is a UX dead-end.
 */
export function useMarketSwitch(): (market: MarketId) => void {
  const setSelectedMarket = useSettingsStore((s) => s.setSelectedMarket);
  const currentMarket = useSettingsStore((s) => s.selectedMarket);
  const setSelectedSymbol = useSettingsStore((s) => s.setSelectedSymbol);
  const resetPortfolios = usePortfoliosStore((s) => s.reset);
  const resetBuilder = useStrategyBuilderStore((s) => s.reset);
  const resetBacktest = useBacktestStore((s) => s.reset);
  const setStrategies = useStrategyStore((s) => s.setStrategies);
  const setTrades = useStrategyStore((s) => s.setTrades);
  const setSignals = useStrategyStore((s) => s.setSignals);
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (next: MarketId) => {
      if (next === currentMarket) return;
      setSelectedMarket(next);
      // Clear anything that was scoped to the previous market.
      setSelectedSymbol(null);
      setStrategies([]);
      setTrades([]);
      setSignals([]);
      resetBuilder();
      resetBacktest();
      // Paper portfolio selection is spot-only; drop it on any switch so
      // the header switcher + capability gate refresh on return-to-spot.
      resetPortfolios();
      // If the user is on a spot-only page and switches to margin/futures,
      // preserve their nav context by trimming the URL to the route root
      // (drops query params + subpaths that belonged to the previous
      // market). The page's own early-return then renders the LockedState.
      // e.g. /backtest?run=abc -> /backtest, /wallet?id=xyz -> /wallet.
      if (next !== 'spot') {
        const root = spotOnlyRootFor(location.pathname);
        if (root) {
          const alreadyAtRoot =
            location.pathname === root &&
            location.search === '' &&
            location.hash === '';
          if (!alreadyAtRoot) navigate(root);
        }
      }
    },
    [
      currentMarket,
      location.pathname,
      navigate,
      resetBacktest,
      resetBuilder,
      resetPortfolios,
      setSelectedMarket,
      setSelectedSymbol,
      setSignals,
      setStrategies,
      setTrades,
    ],
  );
}
